import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import type { AiToneAndVoiceSettings } from "@redotech/redo-concierge-settings-common/ai-tone-and-voice";
import { aiToneAndVoiceSettingsDefault } from "@redotech/redo-concierge-settings-common/ai-tone-and-voice";
import { OrderTrackingTriggerKey } from "@redotech/redo-flows-common/triggers";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import type { TriggerKey } from "@redotech/redo-shopper-agent-common/trigger-definition";
import { ALL_TRIGGERS } from "@redotech/redo-shopper-agent-common/triggers";
import { DEFAULT_SHOPPER_AGENT_PERSONA_NAME } from "@redotech/redo-shopper-agent-settings-common/persona-config";
import { Flex } from "@redotech/redo-web/flex";
import classNames from "classnames";
import { AnimatePresence, motion } from "motion/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as css from "../../../components/intake-modal/intake-modal.module.css";
import type { CustomIntakeExperienceProps } from "../../../registry/types";
import { CompletionStep } from "./completion-step";
import { EscalationStep } from "./escalation-step";
import {
  buildIntakeAnswers,
  getEscalationValidationMessage,
  getNormalizedAdminPhone,
  getPhoneStepValidationMessages,
  isPhoneStepValid,
  needsAddress,
  toEscalationConfigInput,
  toToneSettingsInput,
} from "./helpers";
import { PersonaStep } from "./persona-step";
import { PhoneReadinessStep } from "./phone-readiness-step";
import { ToneVoiceStep } from "./tone-voice-step";
import { TriggerCategoriesStep } from "./trigger-categories-step";
import type {
  ConciergeBusinessInfo,
  ConciergeEscalation,
  ConciergePhoneReadiness,
  ConciergePhoneStatus,
  ConciergeProfileInfo,
  ConciergeStep,
  PhoneValidationField,
} from "./types";
import { WelcomeStep } from "./welcome-step";

export const ConciergeIntakeExperience = memo(
  function ConciergeIntakeExperience({
    onChromeChange,
    onSubmit,
  }: CustomIntakeExperienceProps) {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const marketingRpcClient = useRequiredContext(
      RedoMarketingRpcClientContext,
    );
    const routes = useRequiredContext(MerchantRoutesContext);
    const reloadTeam = useRequiredContext(ReloadTeamContext);
    const team = useRequiredContext(TeamContext);
    const navigate = useNavigate();

    const [stepIndex, setStepIndex] = useState(0);
    const [navigationDirection, setNavigationDirection] = useState<1 | -1>(1);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTriggers, setSelectedTriggers] = useState<TriggerKey[]>([
      OrderTrackingTriggerKey.ORDER_CREATED,
      OrderTrackingTriggerKey.ORDER_DELIVERED,
    ]);
    const [personaName, setPersonaName] = useState(
      DEFAULT_SHOPPER_AGENT_PERSONA_NAME,
    );
    const [toneSettings, setToneSettings] = useState<AiToneAndVoiceSettings>(
      aiToneAndVoiceSettingsDefault,
    );
    const [businessInfo, setBusinessInfo] = useState<ConciergeBusinessInfo>({
      legalEntityName: "",
      ein: "",
      brandName: team.name ?? "",
      supportEmail: "",
    });
    const [profileInfo, setProfileInfo] = useState<ConciergeProfileInfo>({
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
      adminName: "",
      adminPhone: "",
    });
    const [escalation, setEscalation] = useState<ConciergeEscalation>({
      escalateToSupport: null,
      supportEmail: "",
    });
    const [escalationEmailTouched, setEscalationEmailTouched] = useState(false);
    const [phoneStatusOverride, setPhoneStatusOverride] =
      useState<ConciergePhoneStatus | null>(null);
    const [touchedPhoneFields, setTouchedPhoneFields] = useState<
      Partial<Record<PhoneValidationField, true>>
    >({});
    const [showPhoneStep, setShowPhoneStep] = useState(false);
    const [activationState, setActivationState] = useState<{
      phoneStatus: ConciergePhoneStatus;
    } | null>(null);
    const seenStepsRef = useRef<Set<ConciergeStep>>(new Set());
    const currentStepRef = useRef<ConciergeStep | null>(null);
    const currentStepShouldAnimateItemsRef = useRef(true);

    const phoneReadinessLoad = useLoad<ConciergePhoneReadiness>(async () => {
      return merchantRpcClient.getConciergePhoneReadiness({});
    }, [merchantRpcClient]);
    const toneInitLoad = useLoad(
      async () => merchantRpcClient.initializeAiToneAndVoice({}),
      [merchantRpcClient],
    );

    const phoneDefaultsInitialized = useRef(false);
    useEffect(() => {
      if (!phoneReadinessLoad.value || phoneDefaultsInitialized.current) {
        return;
      }
      phoneDefaultsInitialized.current = true;
      setShowPhoneStep(
        phoneReadinessLoad.value.provisioningStatus !== "approved",
      );
      const { merchantProfile } = phoneReadinessLoad.value;
      setProfileInfo((current) => ({
        ...current,
        street1: merchantProfile.street1 ?? "",
        street2: merchantProfile.street2 ?? "",
        city: merchantProfile.city ?? "",
        state: merchantProfile.state ?? "",
        zip: merchantProfile.zip ?? "",
        country: merchantProfile.country ?? current.country,
        adminName: merchantProfile.adminName ?? "",
        adminPhone: merchantProfile.adminPhone ?? "",
      }));
    }, [phoneReadinessLoad.value]);

    const toneDefaultsInitialized = useRef(false);
    useEffect(() => {
      const settings = toneInitLoad.value?.settings;
      if (!settings || toneDefaultsInitialized.current) {
        return;
      }
      toneDefaultsInitialized.current = true;
      setToneSettings({
        voiceInstructions: settings.voiceInstructions ?? "",
        formalityCasualness: settings.formalityCasualness,
        seriousnessHumor: settings.seriousnessHumor,
        respectfulnessIrreverence: settings.respectfulnessIrreverence,
        matterOfFactEnthusiasm: settings.matterOfFactEnthusiasm,
      });
    }, [toneInitLoad.value]);

    const effectivePhoneStatus =
      phoneStatusOverride ??
      phoneReadinessLoad.value?.provisioningStatus ??
      "none";

    const steps: ConciergeStep[] = showPhoneStep
      ? [
          "welcome",
          "phone",
          "triggers",
          "tone",
          "escalation",
          "persona",
          "complete",
        ]
      : ["welcome", "triggers", "tone", "escalation", "persona", "complete"];
    const step = steps[stepIndex] ?? "welcome";
    if (currentStepRef.current !== step) {
      currentStepRef.current = step;
      currentStepShouldAnimateItemsRef.current =
        !seenStepsRef.current.has(step);
    }
    const animateStepItems = currentStepShouldAnimateItemsRef.current;
    const stepUsesInternalScroll = step === "triggers" || step === "tone";

    useEffect(() => {
      seenStepsRef.current.add(step);
    }, [step]);

    const suggestedSupportEmail =
      businessInfo.supportEmail?.trim() || team.customer_email?.trim() || "";
    useEffect(() => {
      if (step !== "escalation") {
        return;
      }
      setEscalation((current) =>
        current.supportEmail.trim() === "" && suggestedSupportEmail
          ? { ...current, supportEmail: suggestedSupportEmail }
          : current,
      );
    }, [step, suggestedSupportEmail]);

    const escalationEmailError =
      escalationEmailTouched && escalation.escalateToSupport === false
        ? getEscalationValidationMessage(escalation)
        : null;

    const missingMerchantFields = useMemo(
      () => phoneReadinessLoad.value?.missingMerchantFields ?? [],
      [phoneReadinessLoad.value?.missingMerchantFields],
    );
    const isPhoneFormRequired =
      showPhoneStep &&
      (effectivePhoneStatus === "none" || effectivePhoneStatus === "rejected");
    const phoneValidationErrors = useMemo(
      () =>
        step === "phone" && isPhoneFormRequired
          ? getPhoneStepValidationMessages({
              businessInfo,
              missingMerchantFields,
              profileInfo,
            })
          : [],
      [
        businessInfo,
        isPhoneFormRequired,
        missingMerchantFields,
        profileInfo,
        step,
      ],
    );
    const visiblePhoneValidationErrors = useMemo(
      () =>
        Object.fromEntries(
          phoneValidationErrors
            .filter((error) => touchedPhoneFields[error.field])
            .map((error) => [error.field, error.message]),
        ) as Partial<Record<PhoneValidationField, string>>,
      [phoneValidationErrors, touchedPhoneFields],
    );

    useEffect(() => {
      if (process.env.NODE_ENV === "production" || step !== "phone") {
        return;
      }
      console.debug("Concierge phone step validation", {
        businessInfo,
        continueDisabled:
          submitting ||
          (isPhoneFormRequired && phoneValidationErrors.length > 0),
        effectivePhoneStatus,
        isPhoneFormRequired,
        missingMerchantFields,
        phoneValidationErrors,
        showPhoneStep,
        touchedPhoneFields,
        visiblePhoneValidationErrors,
      });
    }, [
      businessInfo,
      effectivePhoneStatus,
      isPhoneFormRequired,
      missingMerchantFields,
      phoneValidationErrors,
      showPhoneStep,
      step,
      submitting,
      touchedPhoneFields,
      visiblePhoneValidationErrors,
    ]);

    const continueText = (() => {
      // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
      switch (step) {
        case "complete":
          return "Go to Commerce Agent";
        case "persona":
          return "Turn on Commerce Agent";
        default:
          return "Continue";
      }
    })();

    const continueDisabled = (() => {
      if (submitting) {
        return true;
      }
      if (step === "welcome" && phoneReadinessLoad.pending) {
        return true;
      }
      if (step === "tone" && toneInitLoad.pending) {
        return true;
      }
      if (step === "phone" && isPhoneFormRequired) {
        return !isPhoneStepValid({
          businessInfo,
          missingMerchantFields,
          profileInfo,
        });
      }
      if (step === "escalation") {
        return getEscalationValidationMessage(escalation) !== null;
      }
      return false;
    })();

    const handleBack = useCallback(() => {
      setError(null);
      setNavigationDirection(-1);
      setStepIndex((current) => Math.max(current - 1, 0));
    }, []);

    const handlePhoneValidationFieldBlur = (field: PhoneValidationField) => {
      setTouchedPhoneFields((current) =>
        current[field] ? current : { ...current, [field]: true },
      );
    };

    const handleContinue = useCallback(async () => {
      setError(null);

      if (step === "complete") {
        setSubmitting(true);
        try {
          await onSubmit(
            buildIntakeAnswers({
              selectedTriggers,
              personaName,
              phoneStatus: activationState?.phoneStatus ?? effectivePhoneStatus,
            }),
          );
          navigate(routes.shopperAgentHref);
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : "We couldn't finish onboarding. Please try again.",
          );
        } finally {
          setSubmitting(false);
        }
        return;
      }

      if (step === "phone" && isPhoneFormRequired) {
        setSubmitting(true);
        try {
          const addressComplete = Boolean(
            profileInfo.street1.trim() &&
            profileInfo.city.trim() &&
            profileInfo.state.trim() &&
            profileInfo.zip.trim() &&
            profileInfo.country.trim(),
          );
          const adminName = profileInfo.adminName.trim();
          const adminPhone = getNormalizedAdminPhone(profileInfo.adminPhone);
          const result = await merchantRpcClient.provisionConciergePhoneNumber({
            businessInfo: {
              legalEntityName: businessInfo.legalEntityName.trim(),
              ein: businessInfo.ein.trim(),
              brandName: businessInfo.brandName?.trim() || undefined,
              supportEmail: businessInfo.supportEmail?.trim() || undefined,
            },
            address:
              needsAddress(missingMerchantFields) && addressComplete
                ? {
                    street1: profileInfo.street1.trim(),
                    street2: profileInfo.street2.trim() || undefined,
                    city: profileInfo.city.trim(),
                    state: profileInfo.state.trim(),
                    zip: profileInfo.zip.trim(),
                    country: profileInfo.country.trim(),
                  }
                : undefined,
            adminName:
              missingMerchantFields.includes("adminName") && adminName
                ? adminName
                : undefined,
            adminPhone:
              missingMerchantFields.includes("adminPhone") && adminPhone
                ? adminPhone
                : undefined,
          });

          if (result.status === "blocked") {
            setError(
              result.detail ??
                "We couldn't start phone provisioning yet. Please review the form and try again.",
            );
            return;
          }

          setPhoneStatusOverride(
            result.status === "completed" ? "approved" : "pending",
          );
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : "We couldn't start phone provisioning. Please try again.",
          );
          return;
        } finally {
          setSubmitting(false);
        }
      }

      if (step === "persona") {
        setSubmitting(true);
        try {
          await merchantRpcClient.updateAiToneAndVoiceSettings(
            toToneSettingsInput(toneSettings),
          );

          const selectedTriggerSet = new Set(selectedTriggers);
          const categoriesToEnable = [
            ...new Set(
              ALL_TRIGGERS.filter((t) => selectedTriggerSet.has(t.id)).map(
                (t) => t.category,
              ),
            ),
          ];

          if (categoriesToEnable.length > 0) {
            await marketingRpcClient.bulkEnableShopperAgentCategory({
              categories: categoriesToEnable,
              stageDraft: false,
            });
          }

          // Disable triggers in those categories that weren't explicitly selected
          const triggersToDisable = ALL_TRIGGERS.filter(
            (t) =>
              categoriesToEnable.includes(t.category) &&
              !selectedTriggerSet.has(t.id),
          ).map((t) => t.id);

          await Promise.all(
            triggersToDisable.map((triggerId) =>
              marketingRpcClient.updateShopperAgentTrigger({
                triggerId,
                config: { enabled: false },
                stageDraft: false,
              }),
            ),
          );

          await marketingRpcClient.updateBaseShopperAgentSettings({
            enabled: true,
            iMessageConfig: {
              enabled: team.settings?.shopperAgentAdmin?.allowIMessage === true,
            },
            ...(personaName.trim()
              ? { personaConfig: { name: personaName.trim() } }
              : {}),
            recoverConfig: { enabled: true },
            escalationConfig: toEscalationConfigInput(escalation),
          });

          await reloadTeam();
          setActivationState({
            phoneStatus:
              effectivePhoneStatus === "approved" ? "approved" : "pending",
          });
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : "We couldn't activate Commerce Agent. Please try again.",
          );
          return;
        } finally {
          setSubmitting(false);
        }
      }

      setNavigationDirection(1);
      setStepIndex((current) => Math.min(current + 1, steps.length - 1));
    }, [
      activationState?.phoneStatus,
      businessInfo,
      effectivePhoneStatus,
      escalation,
      isPhoneFormRequired,
      marketingRpcClient,
      merchantRpcClient,
      missingMerchantFields,
      navigate,
      onSubmit,
      personaName,
      profileInfo,
      reloadTeam,
      routes,
      selectedTriggers,
      step,
      steps.length,
      team.settings?.shopperAgentAdmin?.allowIMessage,
      toneSettings,
    ]);

    useEffect(() => {
      onChromeChange({
        backText: "Back",
        continueDisabled,
        continuePending: submitting,
        continueText,
        currentStepIndex: stepIndex,
        footerError: step !== "phone" ? error : null,
        onContinue: handleContinue,
        totalSteps: steps.length,
        ...(stepIndex > 0 && step !== "complete" ? { onBack: handleBack } : {}),
      });
    }, [
      continueDisabled,
      continueText,
      error,
      handleBack,
      handleContinue,
      onChromeChange,
      step,
      stepIndex,
      steps.length,
      submitting,
    ]);

    return (
      <div
        className={classNames(
          css.stepInner,
          css.stepInnerFullWidth,
          stepUsesInternalScroll && css.stepInnerLockScroll,
        )}
      >
        <AnimatePresence custom={navigationDirection} mode="wait">
          <motion.div
            animate={{ opacity: 1, x: 0 }}
            className={`${css.stepCenteredContent} ${css.stepTransitionFrame}`}
            custom={navigationDirection}
            exit={{ opacity: 0, x: navigationDirection === 1 ? -32 : 32 }}
            initial={{ opacity: 0, x: navigationDirection === 1 ? 32 : -32 }}
            key={step}
            transition={{ duration: 0.24, ease: "easeOut" }}
          >
            <Flex
              dir="column"
              gap="3xl"
              height="full"
              justify="center"
              w="full"
            >
              {step === "welcome" ? (
                <WelcomeStep
                  animateItems={animateStepItems}
                  stepNumber={stepIndex + 1}
                />
              ) : null}
              {step === "phone" ? (
                <PhoneReadinessStep
                  animateItems={animateStepItems}
                  businessInfo={businessInfo}
                  effectivePhoneStatus={effectivePhoneStatus}
                  error={error}
                  iMessageAvailable={
                    team.settings?.shopperAgentAdmin?.allowIMessage === true
                  }
                  missingMerchantFields={missingMerchantFields}
                  onValidationFieldBlur={handlePhoneValidationFieldBlur}
                  phoneReadiness={phoneReadinessLoad.value}
                  phoneReadinessLoadPending={phoneReadinessLoad.pending}
                  profileInfo={profileInfo}
                  setBusinessInfo={setBusinessInfo}
                  setProfileInfo={setProfileInfo}
                  stepNumber={stepIndex + 1}
                  validationErrors={visiblePhoneValidationErrors}
                />
              ) : null}
              {step === "triggers" ? (
                <TriggerCategoriesStep
                  animateItems={animateStepItems}
                  selectedTriggers={selectedTriggers}
                  setSelectedTriggers={setSelectedTriggers}
                  stepNumber={stepIndex + 1}
                />
              ) : null}
              {step === "tone" ? (
                <ToneVoiceStep
                  animateItems={animateStepItems}
                  setToneSettings={setToneSettings}
                  stepNumber={stepIndex + 1}
                  teamName={team.name ?? "your brand"}
                  toneInitPending={toneInitLoad.pending}
                  toneSettings={toneSettings}
                />
              ) : null}
              {step === "escalation" ? (
                <EscalationStep
                  animateItems={animateStepItems}
                  emailError={escalationEmailError}
                  escalation={escalation}
                  onEmailBlur={() => setEscalationEmailTouched(true)}
                  setEscalation={setEscalation}
                  stepNumber={stepIndex + 1}
                />
              ) : null}
              {step === "persona" ? (
                <PersonaStep
                  animateItems={animateStepItems}
                  personaName={personaName}
                  setPersonaName={setPersonaName}
                  stepNumber={stepIndex + 1}
                />
              ) : null}
              {step === "complete" ? (
                <CompletionStep
                  activationPhoneStatus={activationState?.phoneStatus ?? null}
                  animateItems={animateStepItems}
                  iMessageAvailable={
                    team.settings?.shopperAgentAdmin?.allowIMessage === true
                  }
                />
              ) : null}
            </Flex>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  },
);
